// =============================================================================
// Workspace Manager - 集成终端和聊天的工作空间管理组件
// =============================================================================

import React, { useState, useEffect, useRef } from 'react';
import {
  Terminal,
  MessageSquare,
  Activity,
  Plus,
  Send,
  Bot,
  User,
  Trash2,
  Play,
  Square
} from 'lucide-react';
import { Terminal as XTerm } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import LoadingSpinner from './LoadingSpinner';
import toast from 'react-hot-toast';
import SocketIOTerminal from './SocketIOTerminal';
import CreateTerminalDialog from './CreateTerminalDialog';
import TerminalTypeIndicator from './TerminalTypeIndicator';
import { apiCall, apiGet, apiPost, apiDelete, extractApiData } from '../utils/apiUtils.js';
import 'xterm/css/xterm.css';

// 精确匹配终端的辅助函数
const isTerminalActive = (terminal, activeTerminal) => {
  if (!activeTerminal || !terminal) return false;

  // 优先使用 terminalId 进行匹配（最可靠）
  const activeId = activeTerminal.terminalId || activeTerminal.id;
  const terminalId = terminal.terminalId || terminal.id;

  const isActive = activeId === terminalId;

  // 调试日志（只在匹配时输出，避免日志过多）
  if (isActive) {
    console.log('🎯 Terminal match found:', {
      terminal: { id: terminal.id, terminalId: terminal.terminalId },
      activeTerminal: { id: activeTerminal.id, terminalId: activeTerminal.terminalId },
      activeId,
      terminalId,
      isActive
    });
  }

  return isActive;
};

const WorkspaceManager = ({ workspace, onWorkspaceUpdate }) => {
  // 状态管理
  const [activeTab, setActiveTab] = useState('overview');
  const [terminals, setTerminals] = useState([]);
  const [activeTerminal, setActiveTerminal] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [resources, setResources] = useState(null);
  
  // 加载状态
  const [isCreatingTerminal, setIsCreatingTerminal] = useState(false);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [useSocketIO, setUseSocketIO] = useState(true); // Default to Socket.IO for better reliability
  const [showCreateTerminalDialog, setShowCreateTerminalDialog] = useState(false);

  // Refs
  const terminalRef = useRef(null);
  const xtermRef = useRef(null);
  const fitAddonRef = useRef(null);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (workspace && workspace.status === 'running') {
      loadTerminals();
      loadConversations();
      loadResources();

      const interval = setInterval(loadResources, 5000);
      return () => clearInterval(interval);
    }
  }, [workspace]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (xtermRef.current) {
        xtermRef.current.dispose();
      }
    };
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // API 调用函数
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      console.warn('No auth token found, user may need to login again');
      // 可以在这里触发重新登录
      window.location.href = '/login';
      return {};
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // 移除了本地的 apiCall 函数，现在使用 utils/apiUtils.js 中的通用函数

  const loadResources = async () => {
    if (!workspace || workspace.status !== 'running') return;

    try {
      const data = await apiGet(`/api/workspaces/${workspace.id}/resources`);
      if (data.success) {
        setResources(data.data);
      }
    } catch (error) {
      console.error('Failed to load resources:', error);
    }
  };

  const loadTerminals = async () => {
    try {
      const data = await apiGet(`/api/terminals/workspace/${workspace.id}`);
      if (data.success) {
        // 确保每个终端对象都有正确的属性
        const normalizedTerminals = data.data.map(terminal => ({
          ...terminal,
          terminalId: terminal.terminalId || terminal.id,
          id: terminal.id || terminal.terminalId
        }));
        console.log('🔧 WorkspaceManager: Loaded terminals:', normalizedTerminals);
        setTerminals(normalizedTerminals);
      }
    } catch (error) {
      console.error('Failed to load terminals:', error);
    }
  };

  const handleDeleteTerminal = async (terminalId) => {
    // 确认删除
    if (!window.confirm('确定要删除这个终端吗？这个操作不可撤销。')) {
      return;
    }

    try {
      console.log(`🗑️ Deleting terminal: ${terminalId}`);

      const response = await apiCall(`/api/terminals/${terminalId}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        toast.success('终端已删除');

        // 如果删除的是当前活跃终端，清除活跃状态
        if (activeTerminal && (activeTerminal.id === terminalId || activeTerminal.terminalId === terminalId)) {
          setActiveTerminal(null);
        }

        // 重新加载终端列表
        await loadTerminals();

        console.log(`✅ Terminal ${terminalId} deleted successfully`);
      } else {
        throw new Error(data.error || 'Failed to delete terminal');
      }
    } catch (error) {
      console.error('Failed to delete terminal:', error);
      toast.error(`删除终端失败: ${error.message}`);
    }
  };

  const handleBatchDeleteTerminals = async (terminalIds, force = false) => {
    if (!Array.isArray(terminalIds) || terminalIds.length === 0) {
      return;
    }

    // 确认批量删除
    const message = `确定要删除 ${terminalIds.length} 个终端吗？这个操作不可撤销。`;
    if (!window.confirm(message)) {
      return;
    }

    try {
      console.log(`🗑️ Batch deleting ${terminalIds.length} terminals:`, terminalIds);

      const response = await apiCall('/api/terminals', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          terminalIds,
          force
        })
      });

      const data = await response.json();

      if (data.success) {
        const { success, failed, errors } = data.data;

        if (success > 0) {
          toast.success(`成功删除 ${success} 个终端`);
        }

        if (failed > 0) {
          toast.error(`${failed} 个终端删除失败`);
          console.error('Batch delete errors:', errors);
        }

        // 如果当前活跃终端被删除，清除活跃状态
        if (activeTerminal && terminalIds.includes(activeTerminal.id || activeTerminal.terminalId)) {
          setActiveTerminal(null);
        }

        // 重新加载终端列表
        await loadTerminals();

        console.log(`✅ Batch delete completed: ${success} success, ${failed} failed`);
      } else {
        throw new Error(data.error || 'Failed to batch delete terminals');
      }
    } catch (error) {
      console.error('Failed to batch delete terminals:', error);
      toast.error(`批量删除失败: ${error.message}`);
    }
  };

  const createTerminal = async (terminalConfig = {}) => {
    console.log('🔧 WorkspaceManager: createTerminal called:', {
      workspaceId: workspace.id,
      workspaceStatus: workspace.status,
      isCreatingTerminal,
      useSocketIO,
      terminalConfig,
      timestamp: new Date().toISOString()
    });

    setIsCreatingTerminal(true);
    try {
      console.log('🔧 WorkspaceManager: Sending terminal creation request...');
      const requestBody = {
        workspaceId: workspace.id,
        cols: terminalConfig.cols || 80,
        rows: terminalConfig.rows || 24,
        shell: terminalConfig.shell || '/bin/bash',
        terminalType: terminalConfig.terminalType || 'general',
        name: terminalConfig.name
      };

      const response = await apiCall('/api/terminals', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      console.log('🔧 WorkspaceManager: Terminal creation response received');
      const data = await response.json();
      console.log('🔧 WorkspaceManager: Terminal creation data:', data);

      if (data.success) {
        console.log('🔧 WorkspaceManager: Terminal created successfully:', data.data);

        console.log('🔧 WorkspaceManager: Loading terminals...');
        await loadTerminals();

        // Only initialize xterm if not using Socket.IO
        if (!useSocketIO) {
          console.log('🔧 WorkspaceManager: Initializing xterm (not using Socket.IO)');
          setActiveTerminal(data.data);
          initializeXterm(data.data.terminalId, true); // true for new terminal
        } else {
          console.log('🔧 WorkspaceManager: Using Socket.IO, setting new terminal as active');
          // 对于 Socket.IO 模式，直接设置新终端为活跃
          // 确保终端对象有正确的属性
          const newTerminal = {
            ...data.data,
            id: data.data.terminalId,
            terminalId: data.data.terminalId
          };
          console.log('🔧 WorkspaceManager: New terminal object:', newTerminal);
          setActiveTerminal(newTerminal);
        }

        console.log('🔧 WorkspaceManager: Setting active tab to terminal');
        setActiveTab('terminal');
        toast.success('Terminal created');
      } else {
        console.error('❌ WorkspaceManager: Terminal creation failed:', data.error);
        toast.error(data.error || 'Failed to create terminal');
      }
    } catch (error) {
      console.error('❌ WorkspaceManager: Error creating terminal:', error);
      toast.error('Failed to create terminal');
    } finally {
      console.log('🔧 WorkspaceManager: Terminal creation finished');
      setIsCreatingTerminal(false);
    }
  };

  const initializeXterm = (terminalId, isNewTerminal = false) => {
    console.log('🚨 initializeXterm called:', { terminalId, isNewTerminal, useSocketIO, stack: new Error().stack });

    if (xtermRef.current) {
      xtermRef.current.dispose();
    }

    // Close existing WebSocket connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    const terminal = new XTerm({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4'
      }
    });

    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    xtermRef.current = terminal;
    fitAddonRef.current = fitAddon;

    if (terminalRef.current) {
      terminal.open(terminalRef.current);
      fitAddon.fit();
    }

    // Connect to WebSocket
    connectToTerminal(terminal, terminalId, isNewTerminal);

    // Handle terminal input
    terminal.onData((data) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'input',
          data: data
        }));
      }
    });

    // Handle terminal resize
    terminal.onResize(({ cols, rows }) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'resize',
          cols: cols,
          rows: rows
        }));
      }
    });
  };

  const connectToTerminal = (terminal, terminalId, isNewTerminal = false, retryCount = 0) => {
    console.log('🚨 connectToTerminal called:', { terminalId, isNewTerminal, retryCount, useSocketIO, stack: new Error().stack });

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const token = localStorage.getItem('auth_token');

    if (!token) {
      terminal.writeln('\r\n❌ No authentication token found');
      return;
    }

    // Prefer direct backend in dev to avoid proxy WS quirks; otherwise use current host
    const isDev = window.location.port === '3001';
    const host = window.location.hostname;
    const targetHost = isDev ? `${host}:3000` : window.location.host;
    const wsUrl = `${wsProtocol}//${targetHost}/shell?token=${encodeURIComponent(token)}&terminalId=${terminalId}&workspaceId=${workspace.id}&action=connect`;

    console.log('🔌 Attempting WebSocket connection:', wsUrl.replace(token, '***'));
    console.log(`🔄 Retry count: ${retryCount}`);

    // Only show connection messages for new terminals or first retry
    if (isNewTerminal || retryCount === 0) {
      terminal.writeln('🔌 Connecting to terminal...');
      if (retryCount > 0) {
        terminal.writeln(`🔄 Retry attempt ${retryCount}...`);
      }
    }

    // Close existing connection if any
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close();
    }

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    // Set connection timeout
    const connectionTimeout = setTimeout(() => {
      if (ws.readyState === WebSocket.CONNECTING) {
        console.log('⏰ WebSocket connection timeout');
        ws.close();
        terminal.writeln('\r\n⏰ Connection timeout');

        // Retry with exponential backoff
        if (retryCount < 5) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 10000);
          setTimeout(() => {
            connectToTerminal(terminal, terminalId, false, retryCount + 1);
          }, delay);
        } else {
          terminal.writeln('\r\n❌ Max retry attempts reached');
        }
      }
    }, 10000); // 10 second timeout

    ws.onopen = () => {
      clearTimeout(connectionTimeout);
      console.log('✅ WebSocket connection opened');
      console.log(`🔗 Connection state: ${ws.readyState}`);
      // Don't show any message here - wait for server's 'connected' message
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('📨 Received message:', message.type, message);

        switch (message.type) {
          case 'output':
          case 'data':
            // Handle both 'output' and 'data' message types for terminal output
            terminal.write(message.data);
            break;
          case 'connected':
            // Terminal connection established
            console.log('✅ Terminal connected:', message.data);

            // Use server-provided information to determine if this is a new connection
            const isNewConnection = message.data.isNewConnection || !message.data.hasProcess;

            if (isNewConnection) {
              // New terminal or terminal without process - show welcome message
              terminal.clear();
              terminal.writeln('✅ Terminal connected successfully!');
              terminal.writeln(`📋 Terminal ID: ${message.data.terminalId}`);
              terminal.writeln('💡 Interactive shell ready');
              terminal.writeln('');
            } else {
              // Existing terminal with running process - just show reconnection message
              if (retryCount > 0) {
                terminal.writeln('\r\n✅ Reconnected to terminal');
              }
            }
            break;
          case 'reconnected':
            // Terminal reconnected after exit - no message needed
            break;
          case 'exit':
            // Terminal process exited
            terminal.writeln(`\r\n💀 Terminal exited with code ${message.data.exitCode}, attempting to reconnect...`);
            break;
          case 'error':
            terminal.writeln(`\r\n❌ Error: ${message.message || message.data}`);
            break;
          case 'auth_success':
            // Authentication successful, terminal is ready
            break;
          case 'auth_error':
            terminal.writeln(`\r\n❌ Authentication failed: ${message.message || message.data}`);
            break;
          default:
            console.log('Unknown message type:', message.type, message);
        }
      } catch (error) {
        console.error('❌ Error parsing WebSocket message:', error);
        // Handle plain text messages
        if (typeof event.data === 'string') {
          terminal.write(event.data);
        }
      }
    };

    ws.onclose = (event) => {
      clearTimeout(connectionTimeout);
      console.log(`🔌 WebSocket closed: code=${event.code}, reason=${event.reason}, wasClean=${event.wasClean}`);

      if (event.wasClean) {
        terminal.writeln('\r\n🔌 Terminal connection closed');
      } else {
        terminal.writeln('\r\n❌ Terminal connection lost');

        // Only retry if not too many attempts and terminal is still active
        if (retryCount < 5 && xtermRef.current === terminal) {
          const delay = Math.min(3000 * Math.pow(1.5, retryCount), 15000);
          terminal.writeln(`🔄 Reconnecting in ${delay/1000}s...`);

          setTimeout(() => {
            if (xtermRef.current === terminal) {
              connectToTerminal(terminal, terminalId, false, retryCount + 1);
            }
          }, delay);
        } else {
          terminal.writeln('\r\n❌ Connection failed permanently');
        }
      }
    };

    ws.onerror = (error) => {
      clearTimeout(connectionTimeout);
      console.error('❌ WebSocket error:', error);
      console.error('❌ WebSocket state:', ws.readyState);
      terminal.writeln('\r\n❌ Connection error occurred');

      // More detailed error information
      if (error.type === 'error') {
        terminal.writeln('🔍 This might be a network connectivity issue');
        terminal.writeln('🔍 Please check if the server is running on port 3000');
      }
    };
  };

  const loadConversations = async () => {
    try {
      const response = await apiCall('/api/chat/conversations');
      const data = await response.json();
      if (data.success) {
        setConversations(data.data.conversations || []);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const createConversation = async () => {
    setIsCreatingConversation(true);
    try {
      const response = await apiCall('/api/chat/conversations', {
        method: 'POST',
        body: JSON.stringify({
          workspaceId: workspace.id,
          title: `${workspace.name} Chat - ${new Date().toLocaleTimeString()}`,
          model: 'claude-3-5-sonnet-20241022'
        })
      });

      const data = await response.json();
      if (data.success) {
        setActiveConversation(data.data);
        setMessages([]);
        setConversations(prev => [data.data, ...prev]);
        setActiveTab('chat');
        toast.success('Conversation created');
      } else {
        toast.error(data.error || 'Failed to create conversation');
      }
    } catch (error) {
      toast.error('Failed to create conversation');
    } finally {
      setIsCreatingConversation(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !activeConversation || isSendingMessage) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');
    setIsSendingMessage(true);

    try {
      const response = await apiCall(`/api/chat/conversations/${activeConversation.id}/messages`, {
        method: 'POST',
        body: JSON.stringify({ message: messageToSend })
      });

      const data = await response.json();
      if (data.success) {
        setMessages(prev => [...prev, data.data.message]);
      } else {
        toast.error(data.error || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleWorkspaceAction = async (action) => {
    if (action === 'start') {
      setIsStarting(true);
      try {
        await apiCall(`/api/workspaces/${workspace.id}/start`, {
          method: 'POST'
        });
        toast.success('Workspace started');
        onWorkspaceUpdate?.();
      } catch (error) {
        toast.error('Failed to start workspace');
      } finally {
        setIsStarting(false);
      }
    } else if (action === 'stop') {
      setIsStopping(true);
      try {
        await apiCall(`/api/workspaces/${workspace.id}/stop`, {
          method: 'POST'
        });
        toast.success('Workspace stopped');
        onWorkspaceUpdate?.();
      } catch (error) {
        toast.error('Failed to stop workspace');
      } finally {
        setIsStopping(false);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'text-green-600 bg-green-100 dark:bg-green-900/20';
      case 'stopped': return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20';
      case 'error': return 'text-red-600 bg-red-100 dark:bg-red-900/20';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20';
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* 工作空间控制 */}
            <div className="flex items-center gap-4">
              {workspace.status === 'stopped' ? (
                <button
                  onClick={() => handleWorkspaceAction('start')}
                  disabled={isStarting}
                  className="btn btn-default"
                >
                  {isStarting ? <LoadingSpinner size="sm" /> : <Play className="w-4 h-4" />}
                  {isStarting ? 'Starting...' : 'Start Workspace'}
                </button>
              ) : (
                <button
                  onClick={() => handleWorkspaceAction('stop')}
                  disabled={isStopping}
                  className="btn btn-secondary"
                >
                  {isStopping ? <LoadingSpinner size="sm" /> : <Square className="w-4 h-4" />}
                  {isStopping ? 'Stopping...' : 'Stop Workspace'}
                </button>
              )}
              
              <button
                onClick={createTerminal}
                disabled={workspace.status !== 'running' || isCreatingTerminal}
                className="btn btn-ghost"
              >
                <Terminal className="w-4 h-4 mr-2" />
                New Terminal
              </button>
              
              <button
                onClick={createConversation}
                disabled={isCreatingConversation}
                className="btn btn-ghost"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                New Chat
              </button>
            </div>

            {/* 资源使用情况 */}
            {workspace.status === 'running' && resources && (
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Resource Usage
                  </h3>
                </div>
                <div className="card-content">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-secondary/50 rounded-lg">
                      <div className="text-2xl font-bold text-foreground">{resources.cpu}%</div>
                      <div className="text-sm text-muted-foreground">CPU Usage</div>
                    </div>
                    <div className="text-center p-4 bg-secondary/50 rounded-lg">
                      <div className="text-2xl font-bold text-foreground">{resources.memory.used}MB</div>
                      <div className="text-sm text-muted-foreground">Memory ({resources.memory.percent}%)</div>
                    </div>
                    <div className="text-center p-4 bg-secondary/50 rounded-lg">
                      <div className="text-2xl font-bold text-foreground">
                        {resources.network.rx + resources.network.tx}MB
                      </div>
                      <div className="text-sm text-muted-foreground">Network I/O</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'terminal':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* 终端列表 */}
            <div className="lg:col-span-1">
              <div className="card">
                <div className="card-header flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Terminals</h3>
                  <button
                    onClick={() => setShowCreateTerminalDialog(true)}
                    disabled={isCreatingTerminal}
                    className="btn btn-ghost btn-sm"
                    title="创建新终端"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="card-content">
                  {terminals.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No terminals</p>
                  ) : (
                    <div className="space-y-2">
                      {terminals.map(terminal => (
                        <div
                          key={terminal.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            isTerminalActive(terminal, activeTerminal)
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50'
                          }`}
                          onClick={() => {
                            console.log('🔄 WorkspaceManager: Switching to terminal:', {
                              id: terminal.id,
                              terminalId: terminal.terminalId,
                              name: terminal.name || terminal.shell
                            });
                            console.log('🔄 WorkspaceManager: Current activeTerminal:', {
                              id: activeTerminal?.id,
                              terminalId: activeTerminal?.terminalId,
                              name: activeTerminal?.name
                            });
                            setActiveTerminal(terminal);

                            // Only initialize xterm if not using Socket.IO
                            if (!useSocketIO) {
                              initializeXterm(terminal.terminalId, false); // false for existing terminal
                            } else {
                              console.log('🔄 WorkspaceManager: Using Socket.IO, terminal switch will be handled by SocketIOTerminal component');
                            }
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <div className="text-sm font-medium truncate">
                                  {terminal.name || terminal.shell}
                                </div>
                                <TerminalTypeIndicator
                                  terminalType={terminal.terminalType || 'general'}
                                  persistenceLevel={terminal.persistenceLevel || 'standard'}
                                  tags={terminal.tags || []}
                                  compact={true}
                                />
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {terminal.cols}x{terminal.rows} • {terminal.shell}
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTerminal(terminal.id);
                              }}
                              className="text-red-500 hover:text-red-700 text-sm transition-colors"
                              title="删除终端"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 终端显示 */}
            <div className="lg:col-span-3">
              <div className="card">
                <div className="card-header">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Terminal</h3>
                    <div className="flex items-center space-x-4">
                      {/* Connection Type Toggle */}
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-muted-foreground">
                          Connection:
                        </label>
                        <select
                          value={useSocketIO ? 'socketio' : 'websocket'}
                          onChange={(e) => setUseSocketIO(e.target.value === 'socketio')}
                          className="text-sm border rounded px-2 py-1"
                        >
                          <option value="socketio">Socket.IO (Reliable)</option>
                          <option value="websocket">WebSocket (Legacy)</option>
                        </select>
                      </div>
                      {activeTerminal && (
                        <div className="text-sm text-muted-foreground">
                          ID: {activeTerminal.terminalId}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="card-content">
                  {activeTerminal ? (
                    useSocketIO ? (
                      <>
                        {console.log('🔌 WorkspaceManager: Rendering SocketIOTerminal with:', {
                          workspace: workspace.id,
                          terminalId: activeTerminal.terminalId,
                          useSocketIO,
                          activeTerminal,
                          timestamp: new Date().toISOString()
                        })}
                        {console.log('🔌 WorkspaceManager: Component state:', {
                          terminals: terminals.length,
                          activeTerminal: activeTerminal ? {
                            terminalId: activeTerminal.terminalId,
                            id: activeTerminal.id,
                            name: activeTerminal.name
                          } : null,
                          activeTerminalIndex: terminals.findIndex(t => isTerminalActive(t, activeTerminal)),
                          useSocketIO,
                          workspaceStatus: workspace.status
                        })}
                        <SocketIOTerminal
                          key={`socket-terminal-${activeTerminal.terminalId || activeTerminal.id}-${workspace.id}`}
                          workspace={workspace}
                          terminalId={activeTerminal.terminalId || activeTerminal.id}
                          onTerminalCreated={(terminal) => {
                            console.log('🔌 WorkspaceManager: onTerminalCreated callback called:', terminal);

                            // 确保终端对象有正确的属性
                            const newTerminal = {
                              ...terminal,
                              terminalId: terminal.terminalId || terminal.id,
                              id: terminal.terminalId || terminal.id
                            };

                            console.log('🔌 WorkspaceManager: Setting active terminal from callback:', newTerminal);

                            // 立即设置新终端为活跃
                            setActiveTerminal(newTerminal);

                            // 重新加载终端列表以确保UI同步
                            loadTerminals();
                          }}
                        />
                      </>
                    ) : (
                      <div
                        ref={terminalRef}
                        className="w-full h-96 bg-black rounded"
                        style={{ minHeight: '400px' }}
                      />
                    )
                  ) : (
                    <div className="flex items-center justify-center h-96 bg-secondary/20 rounded">
                      <p className="text-muted-foreground">
                        {useSocketIO
                          ? "Create or select a terminal to get started (Socket.IO mode)"
                          : "Create or select a terminal to get started (WebSocket mode)"
                        }
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'chat':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* 对话列表 */}
            <div className="lg:col-span-1">
              <div className="card">
                <div className="card-header flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Conversations</h3>
                  <button
                    onClick={createConversation}
                    disabled={isCreatingConversation}
                    className="btn btn-ghost btn-sm"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="card-content">
                  {conversations.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No conversations</p>
                  ) : (
                    <div className="space-y-2">
                      {conversations.map(conv => (
                        <div
                          key={conv.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            activeConversation?.id === conv.id
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50'
                          }`}
                          onClick={() => {
                            setActiveConversation(conv);
                            // Load conversation messages
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{conv.title}</div>
                              <div className="text-xs text-muted-foreground">
                                {conv.messageCount} messages
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 聊天界面 */}
            <div className="lg:col-span-3">
              <div className="card h-[600px] flex flex-col">
                <div className="card-header">
                  <h3 className="text-lg font-semibold">Chat with Claude</h3>
                  {activeConversation && (
                    <div className="text-sm text-muted-foreground">
                      {activeConversation.title}
                    </div>
                  )}
                </div>
                
                {activeConversation ? (
                  <>
                    {/* 消息列表 */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {messages.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-muted-foreground">
                          <div className="text-center">
                            <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>Start a conversation with Claude</p>
                          </div>
                        </div>
                      ) : (
                        messages.map((message, index) => (
                          <div
                            key={message.id || index}
                            className={`flex gap-3 ${
                              message.role === 'user' ? 'justify-end' : 'justify-start'
                            }`}
                          >
                            {message.role === 'assistant' && (
                              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                <Bot className="w-4 h-4 text-primary" />
                              </div>
                            )}
                            
                            <div
                              className={`max-w-[70%] p-3 rounded-lg ${
                                message.role === 'user'
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-secondary text-secondary-foreground'
                              }`}
                            >
                              <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                              <div className="text-xs opacity-70 mt-1">
                                {new Date(message.timestamp).toLocaleTimeString()}
                              </div>
                            </div>

                            {message.role === 'user' && (
                              <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4" />
                              </div>
                            )}
                          </div>
                        ))
                      )}
                      {isSendingMessage && (
                        <div className="flex gap-3 justify-start">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <Bot className="w-4 h-4 text-primary animate-pulse" />
                          </div>
                          <div className="bg-secondary text-secondary-foreground p-3 rounded-lg">
                            <div className="text-sm">Claude is thinking...</div>
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* 输入区域 */}
                    <div className="border-t p-4">
                      <div className="flex gap-2">
                        <textarea
                          value={inputMessage}
                          onChange={(e) => setInputMessage(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              sendMessage();
                            }
                          }}
                          placeholder="Type your message... (Enter to send, Shift+Enter for new line)"
                          className="flex-1 input resize-none"
                          rows={2}
                          disabled={isSendingMessage}
                        />
                        <button
                          onClick={sendMessage}
                          disabled={!inputMessage.trim() || isSendingMessage}
                          className="btn btn-default px-4"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Create a conversation to start chatting with Claude</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* 工作空间状态 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(workspace.status)}`}>
            {workspace.status}
          </span>
          <div className="text-sm text-muted-foreground">
            Template: {workspace.templateId}
          </div>
        </div>
      </div>

      {/* 标签页导航 */}
      <div className="border-b">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: 'Overview', icon: Activity },
            { id: 'terminal', label: 'Terminal', icon: Terminal },
            { id: 'chat', label: 'Chat', icon: MessageSquare }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* 标签页内容 */}
      <div className="min-h-96">
        {renderTabContent()}
      </div>

      {/* 创建终端对话框 */}
      <CreateTerminalDialog
        isOpen={showCreateTerminalDialog}
        onClose={() => setShowCreateTerminalDialog(false)}
        onCreateTerminal={createTerminal}
        workspaceId={workspace.id}
      />
    </div>
  );
};

export default WorkspaceManager;
